import type { ReactNode } from "react";
import { Progress } from "@/shared/components/ui/progress";
import { cn } from "@/shared/lib/cn";

const NEAR_LIMIT_PERCENT = 80;

export interface MeterState {
  isUnlimited: boolean;
  /// 0-100, meaningless (0) when unlimited.
  percent: number;
  isNearLimit: boolean;
  /// The text shown next to the label: "Unlimited" or "used / total".
  valueLabel: string;
}

/// Pure resolver so the meter's math and copy are unit-testable without a
/// render harness (this repo has no RTL). Follows the `resolveFeatureEnabled`
/// precedent in `hooks/use-feature-enabled.ts`. `total === null` means
/// unlimited: no bar, no fraction, just the word "Unlimited".
export function resolveMeterState(used: number, total: number | null): MeterState {
  if (total === null) {
    return { isUnlimited: true, percent: 0, isNearLimit: false, valueLabel: "Unlimited" };
  }
  const safeTotal = Math.max(total, 0);
  const percent = safeTotal === 0 ? 100 : Math.min(100, Math.max(0, (used / safeTotal) * 100));
  return {
    isUnlimited: false,
    percent,
    isNearLimit: percent >= NEAR_LIMIT_PERCENT,
    valueLabel: `${used.toLocaleString()} / ${safeTotal.toLocaleString()}`,
  };
}

interface UsageMeterProps {
  /** What the meter measures, shown as the row's leading text (e.g. "AI credits", "Seats"). */
  label: string;
  used: number;
  /** Total allowance. Null means unlimited: no bar renders, just "Unlimited". */
  total: number | null;
  /** "sm" renders a denser row for tight spaces like the sidebar. */
  size?: "sm" | "default";
  className?: string;
}

/**
 * Compact linear usage meter: a label, a "used / total" (or "Unlimited")
 * figure, and a filled bar. The bar turns the warning token color at >=80%
 * used. Color is never the only signal, the figure is always visible text
 * next to it.
 *
 * The bar is marked `aria-hidden`: the value is already conveyed as plain
 * text (same technique `OnboardingProgress` uses for its segment dots), so a
 * screen reader isn't asked to parse a second, redundant progressbar
 * announcement.
 */
export function UsageMeter({
  label,
  used,
  total,
  size = "default",
  className,
}: UsageMeterProps): ReactNode {
  const state = resolveMeterState(used, total);
  const labelTextClass = size === "sm" ? "text-3xs" : "text-xs";

  return (
    <div className={cn("flex flex-col gap-1", className)}>
      <div className="flex items-center justify-between gap-2">
        <span className={cn("truncate text-text-tertiary", labelTextClass)}>{label}</span>
        <span
          className={cn(
            "shrink-0 font-medium tabular-nums",
            labelTextClass,
            state.isNearLimit ? "text-warning-fg" : "text-text-secondary",
          )}
        >
          {state.valueLabel}
        </span>
      </div>
      {state.isUnlimited ? null : (
        <Progress
          value={state.percent}
          aria-hidden="true"
          className={cn(size === "sm" ? "h-1" : "h-1.5", "bg-surface-muted")}
          indicatorClassName={state.isNearLimit ? "bg-warning-fg" : "bg-primary"}
        />
      )}
    </div>
  );
}
